<template>
  <div id="detection" class="tab-content active">
    <div class="glass-card p-8 mb-8">
      <h1 class="text-3xl font-bold mb-4 text-gradient">AIGC优化工具</h1>
      <p class="text-gray-600 mb-6">我们的工具可以帮助您优化文本中的AI生成痕迹，使您的文本更加自然、人性化。</p>

      <div class="mb-6">
        <textarea v-model="inputText" class="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="请输入需要优化的文本..."></textarea>
      </div>

      <div class="flex flex-wrap gap-4">
        <button 
          @click="handleOptimize" 
          :disabled="isProcessing" 
          class="btn-secondary px-6 py-3 rounded-lg font-medium"
        >
          <i class="fas fa-magic mr-2"></i>{{ isProcessing ? '优化中...' : '一键降AI' }}
        </button>
      </div>
    </div>

    <!-- 优化结果模态框 -->
    <div v-if="showOptimizeModal" class="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 active">
      <div class="glass-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-purple-700">
            <i class="fas fa-magic mr-2"></i>优化结果
          </h3>
          <button @click="showOptimizeModal = false" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="mb-4 p-4 bg-purple-50 rounded-lg">
          <div class="flex items-center mb-2">
            <div class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
              <i v-if="!isProcessing" class="fas fa-check text-purple-600"></i>
              <div v-else class="animate-spin">
                <i class="fas fa-circle-notch text-purple-600"></i>
              </div>
            </div>
            <span class="font-medium">优化状态: 
              <span class="text-purple-600">
                <span v-if="isProcessing">{{ currentProcessingText }}</span>
                <span v-else-if="isError">优化失败</span>
                <span v-else>优化完成</span>
              </span>
            </span>
          </div>
          <p class="text-gray-700">{{ isProcessing ? '我们正在为您的文本进行智能优化，这可能需要一点时间...' : (isError ? '出现错误，请重试。' : '您的文本已成功优化，现在更加自然流畅，具有更明显的人类写作特征。') }}</p>
        </div>
        <div class="p-4 bg-white rounded-lg border border-gray-200 mb-4">
          <h4 class="font-medium mb-2 text-gray-700">优化内容:</h4>
          <div class="text-gray-600 whitespace-pre-wrap">
            {{ optimizedText }}
          </div>
        </div>
        <div class="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
          <h4 class="font-medium mb-2 text-gray-700">优化说明:</h4>
          <ul class="space-y-2 text-gray-600">
            <li class="flex items-start">
              <i class="fas fa-circle text-xs text-purple-500 mt-1.5 mr-2"></i>
              <span>调整句式结构，增加表达多样性</span>
            </li>
            <li class="flex items-start">
              <i class="fas fa-circle text-xs text-purple-500 mt-1.5 mr-2"></i>
              <span>加入更自然的口语化表达</span>
            </li>
            <li class="flex items-start">
              <i class="fas fa-circle text-xs text-purple-500 mt-1.5 mr-2"></i>
              <span>增强情感表达和个性化语气</span>
            </li>
            <li class="flex items-start">
              <i class="fas fa-circle text-xs text-purple-500 mt-1.5 mr-2"></i>
              <span>优化段落过渡，使行文更流畅</span>
            </li>
          </ul>
        </div>
        <div class="mt-4 flex justify-end space-x-3">
          <button @click="copyToClipboard" class="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg">
            <i class="fas fa-copy mr-2"></i>复制结果
          </button>
          <button @click="showOptimizeModal = false" class="btn-secondary px-4 py-2 rounded-lg">
            <i class="fas fa-check mr-2"></i>完成
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted, computed } from 'vue'
import { api } from '../utils/api'
import toast from '../utils/toast'
import { authStore } from '../utils/authStore'

const inputText = ref('')
const showOptimizeModal = ref(false)
const optimizedText = ref('优化后的文本将显示在这里...')
const websocket = ref(null)
const sessionId = ref('')
const isProcessing = ref(false)
const isConnecting = ref(false)
const processingTextIndex = ref(0)
const isError = ref(false)

// 优化中的状态文案
const processingTexts = [
  "正在分析文本...",
  "优化文本结构中...",
  "AIGC优化中...",
  "数据渲染中..."
]

// 当前显示的优化状态文案
const currentProcessingText = computed(() => {
  return processingTexts[processingTextIndex.value]
})

// 定时切换优化状态文案
let textSwitchInterval = null

const startTextSwitching = () => {
  // 清除可能存在的旧定时器
  if (textSwitchInterval) {
    clearInterval(textSwitchInterval)
  }
  
  // 重置索引
  processingTextIndex.value = 0
  
  // 创建新的定时器，每3-5秒切换一次文案
  textSwitchInterval = setInterval(() => {
    // 如果已经是最后一个文案，就停止切换
    if (processingTextIndex.value === processingTexts.length - 1) {
      clearInterval(textSwitchInterval)
      textSwitchInterval = null
      return
    }
    
    // 否则切换到下一个文案
    processingTextIndex.value++
  }, 10000 + Math.random() * 5000) // 10-15秒随机
}

const stopTextSwitching = () => {
  if (textSwitchInterval) {
    clearInterval(textSwitchInterval)
    textSwitchInterval = null
  }
  // 直接设置为最后一个文案
  processingTextIndex.value = processingTexts.length - 1
}

// WebSocket连接管理
const connectWebSocket = () => {
  // 如果已经有连接，则不再重新连接
  if (websocket.value) {
    return Promise.resolve()
  }

  const token = localStorage.getItem('token')
  if (!token) {
    authStore.show()
    return Promise.reject(new Error('请先登录'))
  }
  
  isConnecting.value = true
  
  return new Promise((resolve, reject) => {
    const wsUrl = `ws://127.0.0.1:8080/websocket?token=${encodeURIComponent(token)}`
    websocket.value = new WebSocket(wsUrl)
    
    websocket.value.onopen = () => {
      console.log('WebSocket连接已建立')
      isConnecting.value = false
      resolve()
    }
    
    websocket.value.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data)
        if (response.event === 'session_created') {
          sessionId.value = response.data
          console.log('获取到sessionId:', sessionId.value)
          resolve() // 获取到sessionId后解析Promise
        }
        if (response.event === 'send_data') {
          // 确保模态框是打开的
          if (!showOptimizeModal.value) {
            showOptimizeModal.value = true
          }
          
          // 追加新返回的文本
          optimizedText.value += response.data
          console.log('获取到新的文本片段:', response.data)
        }
        if (response.event === 'send_data_complete') {
          // 优化完成
          isProcessing.value = false
          stopTextSwitching() // 停止文案切换
          console.log('优化完成')
        }
      } catch (error) {
        console.error('解析WebSocket消息失败:', error)
      }
    }

    websocket.value.onerror = (error) => {
      console.error('WebSocket连接错误:', error)
      isConnecting.value = false
      reject(error)
    }

    websocket.value.onclose = () => {
      console.log('WebSocket连接已关闭')
      websocket.value = null
      sessionId.value = ''
    }
    
    // 设置超时
    setTimeout(() => {
      if (!sessionId.value && websocket.value) {
        reject(new Error('WebSocket连接超时'))
        cleanupWebSocket()
      }
    }, 10000) // 10秒超时
  })
}

// 清理WebSocket连接
const cleanupWebSocket = () => {
  if (websocket.value) {
    websocket.value.close()
    websocket.value = null
    sessionId.value = ''
  }
}

// 一键降AI功能
const handleOptimize = async () => {
  if (!inputText.value.trim()) {
    toast.show('请输入需要优化的文本')
    return
  }
  
  isProcessing.value = true
  isError.value = false // 重置错误状态
  optimizedText.value = '' // 清空之前的优化结果

  try {
    // 先建立WebSocket连接并等待获取sessionId
    if (!websocket.value || !sessionId.value) {
      await connectWebSocket()
    }
    
    // 确保获取到sessionId
    if (!sessionId.value) {
      // 如果connectWebSocket成功返回但仍然没有sessionId，等待sessionId
      await new Promise((resolve, reject) => {
        const startTime = Date.now()
        const checkSessionId = () => {
          if (sessionId.value) {
            return resolve()
          }
          if (!websocket.value) {
            return reject(new Error('WebSocket连接已关闭'))
          }
          if (Date.now() - startTime > 15000) {
            return reject(new Error('等待sessionId超时'))
          }
          setTimeout(checkSessionId, 500)
        }
        checkSessionId()
      })
    }

    // 连接成功后，显示模态框并开始处理
    showOptimizeModal.value = true
    startTextSwitching()
    
    // 确保获取到sessionId
    if (!sessionId.value) {
      throw new Error('未能获取到会话ID')
    }
    
    optimizedText.value = ''
    
    // 发送优化请求
    await api.fetch(`/api/aigc/clean?sessionId=${sessionId.value}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: inputText.value
    })

    // isProcessing will be set to false upon receiving 'send_data_complete' event.

  } catch (error) {
    console.error('优化请求失败:', error)
    toast.show(`操作失败: ${error.message}`)
    isProcessing.value = false
    isError.value = true
    // 如果模态框已经打开，则在模态框内显示错误，否则就不用管了
    if (showOptimizeModal.value) {
      optimizedText.value = '优化失败，请稍后重试...'
      stopTextSwitching()
    }
  }
}

// 复制到剪贴板功能
const copyToClipboard = () => {
  if (isProcessing.value || isError.value) {
    toast.show('请优化完成后复制')
    return
  }
  navigator.clipboard.writeText(optimizedText.value).then(() => {
    toast.show('文本已复制到剪贴板')
  })
}

onUnmounted(() => {
  cleanupWebSocket()
  stopTextSwitching() // 确保清理定时器
})
</script>
